import { useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { ALL_ASSISTANTS } from "@consts";
import { USER_PERMISSIONS } from "@hyro/dashboard-permissions";
import useAppStore from "src/stores/appStore";
import { hasPermissionSelector } from "src/stores/selectors/appSelectors";
import useGeneralSettings from "./general/GeneralSettings.hook";
import useDeploymentSettings from "./deployment/DeploymentSettings.hook";
import useAnalyticsSettings from "./analytics/AnalyticsSettings.hook";
import { useFetchDashboardSettings } from "src/queries/hooks/settings";

const useAgentSettings = () => {
  const navigate = useNavigate();
  const { assistantId, accountId } = useParams();
  const location = useLocation();
  const rowData = location.state;
  const isAdminContext = location.pathname.includes("/admin/");
  const dashboardSettings = useFetchDashboardSettings()?.data;
  const isReadOnly = !!dashboardSettings?.disableChanges;
  const hasPermission = useAppStore(hasPermissionSelector);
  const canViewDeployment = hasPermission(USER_PERMISSIONS.ABILITIES.DEPLOYMENT_SETTINGS);

  const [feedbackConfig, setFeedbackConfig] = useState({
    show: false,
    message: "",
    severity: "success"
  });

  const initialRow = rowData || { id: assistantId, accountId };

  const {
    isPublishDialogOpen,
    setIsPublishDialogOpen,
    assistantSettingsProps,
    areSettingsChanged: areGeneralSettingsChanged,
    isLoading: isGeneralLoading,
    clearAssistantSettings,
    publishSettings: publishGeneralSettings,
    settingsInitialData
  } = useGeneralSettings(setFeedbackConfig, initialRow, isAdminContext);

  const {
    areSettingsChanged: areDeploymentSettingsChanged,
    isLoading: isDeploymentLoading,
    isSubmitting: isDeploymentSubmitting,
    fetchSettings: fetchDeploymentSettings,
    clear: clearDeploymentSettings,
    submit: submitDeploymentSettings,
    initialData: deploymentInitialData,
    fieldProps: deploymentFieldProps
  } = useDeploymentSettings(setFeedbackConfig, isAdminContext);

  const {
    areSettingsChanged: areAnalyticsSettingsChanged,
    activeSetup,
    setSelectedSetup,
    allSetups,
    benchmarks,
    hasOverrides,
    isLoading: isAnalyticsLoading,
    isPublishing: isAnalyticsPublishing,
    isError: isAnalyticsError,
    onBenchmarkUpdate,
    pendingSetup,
    confirmSetupChange,
    cancelSetupChange,
    valueReferences,
    onValueReferenceUpdate,
    publish: publishAnalyticsSettings,
    clear: clearAnalyticsSettings
  } = useAnalyticsSettings(assistantId, setFeedbackConfig);

  const [hasFetchedDeployment, setHasFetchedDeployment] = useState(false);
  if (canViewDeployment && initialRow?.id && !hasFetchedDeployment && !isDeploymentLoading && !deploymentInitialData) {
    setHasFetchedDeployment(true);
    fetchDeploymentSettings(initialRow);
  }

  const areSettingsChanged =
    areGeneralSettingsChanged
    || areDeploymentSettingsChanged
    || areAnalyticsSettingsChanged;
  const isLoading = isGeneralLoading || isDeploymentSubmitting || isAnalyticsPublishing;

  const navigateBack = () => {
    if (isAdminContext) {
      navigate(`/admin/${ALL_ASSISTANTS}`, { replace: true });
    } else {
      navigate(-1);
    }
  };

  const handlePublish = async () => {
    let allSucceeded = true;
    if (areGeneralSettingsChanged) {
      const generalOk = await publishGeneralSettings();
      if (!generalOk) allSucceeded = false;
    }
    if (areDeploymentSettingsChanged) {
      const deploymentOk = await submitDeploymentSettings();
      if (!deploymentOk) allSucceeded = false;
    }
    if (areAnalyticsSettingsChanged) {
      const analyticsOk = await publishAnalyticsSettings();
      if (!analyticsOk) allSucceeded = false;
    }
    setIsPublishDialogOpen(false);
    if (allSucceeded) {
      navigateBack();
    }
  };

  const handleDiscard = () => {
    clearAssistantSettings();
    clearDeploymentSettings();
    clearAnalyticsSettings();
    navigateBack();
  };

  return {
    assistantId,
    isAdminContext,
    isPublishDialogOpen,
    setIsPublishDialogOpen,
    assistantSettingsProps,
    areSettingsChanged,
    isLoading,
    handlePublish,
    handleDiscard,
    feedbackConfig,
    setFeedbackConfig,
    settingsInitialData,
    deploymentFieldProps,
    isDeploymentSubmitting,
    analyticsSettingsProps: {
      activeSetup,
      setSelectedSetup,
      allSetups,
      benchmarks,
      hasOverrides,
      isLoading: isAnalyticsLoading,
      isError: isAnalyticsError,
      onBenchmarkUpdate,
      pendingSetup,
      confirmSetupChange,
      cancelSetupChange,
      valueReferences,
      onValueReferenceUpdate
    },
    isReadOnly
  };
};

export default useAgentSettings;
